"use client";

import Link from "next/link";
import { sendGTMEvent } from "@next/third-parties/google";
import { ArrowRight, Calculator, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogTrigger, Heading, Modal, ModalOverlay } from "react-aria-components";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePaymentPreferences } from "@/hooks/usePaymentPreferences";
import { getMaximumAffordableVehiclePrice, PAYMENT_CREDIT_PROFILES, PAYMENT_LOAN_TERMS, type PaymentCreditProfileId, type PaymentLoanTerm } from "@/utils/paymentEstimate";
import { usePricing } from "@/components/pricing/PricingProvider";
const MAX_FILTER_PRICE = 100_000;
const CREDIT_PROFILE_IDS = Object.keys(PAYMENT_CREDIT_PROFILES) as PaymentCreditProfileId[];
const formatWholeCurrency = (value: number) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0
}).format(value);
export default function ShopByPaymentCard({
  compact = false,
  onOpen
}: {
  compact?: boolean;
  onOpen?: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    preferences,
    save: savePreferences
  } = usePaymentPreferences();
  const [maximumMonthlyPayment, setMaximumMonthlyPayment] = useState(800);
  const [downPayment, setDownPayment] = useState(4_000);
  const [creditProfileId, setCreditProfileId] = useState<PaymentCreditProfileId>("excellent");
  const [termMonths, setTermMonths] = useState<PaymentLoanTerm>(72);
  // Only the page-level card owns the modal; mobile uses a trigger for the same URL state.
  const isDrawerOpen = !compact && searchParams.get("modal") === "payment";
  const setIsDrawerOpen = (open: boolean) => {
    const url = new URL(window.location.href);
    if (open) url.searchParams.set("modal", "payment");else url.searchParams.delete("modal");
    // UI-only state uses Next.js-integrated history without refetching inventory.
    if (open) window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);else window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  };
  const [matchingVehicleCount, setMatchingVehicleCount] = useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const hasSeededFromPreferences = useRef(false);
  const creditProfile = PAYMENT_CREDIT_PROFILES[creditProfileId];

  // Reopen the drawer with the shopper's saved selections (from here or the VDP).
  useEffect(() => {
    if (hasSeededFromPreferences.current || !preferences?.customized) return;
    hasSeededFromPreferences.current = true;
    setDownPayment(preferences.down);
    setCreditProfileId(preferences.tier);
    setTermMonths(preferences.term);
  }, [preferences]);

  // Mirrors the proof: every selection change updates card estimates immediately.
  const updatePreferences = (next: {
    down?: number;
    term?: PaymentLoanTerm;
    tier?: PaymentCreditProfileId;
  }) => {
    hasSeededFromPreferences.current = true;
    savePreferences({
      down: next.down ?? downPayment,
      term: next.term ?? termMonths,
      tier: next.tier ?? creditProfileId
    });
  };
  const handleDownPaymentChange = (value: number) => {
    setDownPayment(value);
    updatePreferences({
      down: value
    });
  };
  const handleCreditTierChange = (tier: PaymentCreditProfileId) => {
    setCreditProfileId(tier);
    updatePreferences({
      tier
    });
  };
  const handleTermChange = (term: PaymentLoanTerm) => {
    setTermMonths(term);
    updatePreferences({
      term
    });
  };
  const estimatedMaximumPrice = useMemo(() => getMaximumAffordableVehiclePrice({
    maximumMonthlyPayment,
    downPayment,
    termMonths,
    aprPercent: creditProfile.aprPercent
  }), [creditProfile.aprPercent, downPayment, maximumMonthlyPayment, termMonths]);
  const {
    adjustment
  } = usePricing();
  const filterMaximumPrice = Math.max(0, Math.min(MAX_FILTER_PRICE, Math.floor((estimatedMaximumPrice - adjustment) / 100) * 100));
  useEffect(() => {
    if (!isDrawerOpen) return;
    const controller = new AbortController();
    setIsCountLoading(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/cars/payment-count?maximumPrice=${filterMaximumPrice}`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error("Payment inventory count failed");
        const data = (await response.json()) as {
          count?: number;
        };
        setMatchingVehicleCount(typeof data.count === "number" ? data.count : null);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setMatchingVehicleCount(null);
        }
      } finally {
        if (!controller.signal.aborted) setIsCountLoading(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [filterMaximumPrice, isDrawerOpen]);
  const matchingVehicleLabel = matchingVehicleCount === null ? "matching vehicles" : `${matchingVehicleCount} ${matchingVehicleCount === 1 ? "vehicle" : "vehicles"}`;
  const matchingVehicleSummary = matchingVehicleCount === null ? "Inventory count unavailable" : `${matchingVehicleLabel} ${matchingVehicleCount === 1 ? "matches" : "match"} your payment`;
  const handleOpen = () => {
    sendGTMEvent({
      event: "shop_by_payment_open",
      page_section: compact ? "mobile_filters" : "inventory_top"
    });
  };
  const handleApply = () => {
    sendGTMEvent({
      event: "search",
      search_type: "shop_by_payment",
      maximum_monthly_payment: maximumMonthlyPayment,
      down_payment: downPayment,
      sample_apr: creditProfile.aprPercent,
      term_months: termMonths,
      maximum_vehicle_price: filterMaximumPrice
    });
    // Update the filter and dismiss the modal atomically so competing URL writes cannot reopen it.
    const params = new URLSearchParams(searchParams);
    params.delete("modal");
    params.delete("page");
    params.delete("price");
    if (filterMaximumPrice !== MAX_FILTER_PRICE) {
      params.append("price", "0");
      params.append("price", String(filterMaximumPrice));
    }
    const query = params.toString();
    router.replace(`${pathname}${query ? `?${query}` : ""}`, {
      scroll: false
    });
  };
  if (compact) {
    return <button type="button" onClick={() => {
      handleOpen();
      onOpen?.();
      setIsDrawerOpen(true);
    }} aria-haspopup="dialog" className="t-inline-flex t-min-h-11 t-w-full t-items-center t-justify-center t-gap-2 t-bg-brand-violet t-px-5 t-py-3 t-text-sm t-font-bold t-text-white focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-offset-2">
                Shop by payment <ArrowRight className="t-h-4 t-w-4" aria-hidden="true" />
            </button>;
  }
  return <section className="t-border t-border-brand-violet/15 t-bg-brand-lavender/20 t-p-4 sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-6 sm:t-px-5 sm:t-py-4" aria-label="Shop by payment">
            {!compact && <div className="t-min-w-0">
                <p className="t-m-0 t-text-[10px] t-font-black t-uppercase t-tracking-[0.15em] t-text-brand-violet">
                    Shop by payment
                </p>
                <p className="t-m-0 t-mt-1 t-text-base t-font-bold t-leading-snug t-text-automotive-dark">
                    Start with the payment that works for you.
                </p>
            </div>}

            <DialogTrigger isOpen={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                <Button onPress={handleOpen} className={`${compact ? "" : "t-mt-4"} t-inline-flex t-min-h-11 t-w-full t-items-center t-justify-center t-gap-2 t-bg-brand-violet t-px-5 t-py-2.5 t-text-sm t-font-bold t-text-white t-shadow-sm t-transition-colors hover:t-bg-brand-violet-dark pressed:t-bg-brand-violet-dark focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 sm:t-mt-0 sm:t-w-auto sm:t-flex-shrink-0`}>
                    Shop by payment
                    <ArrowRight className="t-h-4 t-w-4" aria-hidden="true" />
                </Button>

                <ModalOverlay isDismissable className="t-fixed t-inset-0 t-z-[2147483647] t-flex t-justify-end t-bg-black/55 t-backdrop-blur-sm entering:t-animate-fade-in exiting:t-animate-fade-out">
                    <Modal className="t-h-dvh t-w-full t-max-w-xl t-overflow-hidden t-bg-white t-shadow-2xl t-outline-none entering:t-animate-slide-in-from-right exiting:t-animate-slide-out-to-right">
                        <Dialog aria-label="Shop for vehicles by estimated payment" className="t-flex t-h-dvh t-flex-col focus:t-outline-none">
                            {({
              close
            }) => <>
                                    <div className="t-relative t-flex-shrink-0 t-bg-automotive-dark t-px-5 t-pb-5 t-pt-6 t-text-white sm:t-px-7 sm:t-pb-6 sm:t-pt-7">
                                        <p className="t-m-0 t-text-[10px] t-font-black t-uppercase t-tracking-[0.15em] t-text-brand-highlighter">
                                            Shop by payment
                                        </p>
                                        <Heading slot="title" className="t-m-0 t-mt-2 t-pr-12 t-text-2xl t-font-bold t-leading-tight t-tracking-[-0.03em] sm:t-text-3xl">
                                            Start with what works for you.
                                        </Heading>
                                        <p className="t-m-0 t-mt-2 t-max-w-xl t-text-sm t-leading-6 t-text-white/75">
                                            Use a few estimates to see vehicles that may fit your monthly budget.
                                        </p>
                                        <Button onPress={close} aria-label="Close payment shopper" className="t-absolute t-right-4 t-top-4 t-flex t-h-11 t-w-11 t-items-center t-justify-center t-rounded-none t-bg-white/10 t-text-white hover:t-bg-white/20 focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-highlighter">
                                            <X className="t-h-5 t-w-5" aria-hidden="true" />
                                        </Button>
                                    </div>

                                    <div className="t-min-h-0 t-flex-1 t-overflow-y-auto t-px-5 t-py-5 sm:t-px-7 sm:t-py-6">
                                        <div className="t-grid t-gap-7">
                                            <label className="t-block">
                                                <span className="t-flex t-items-baseline t-justify-between t-gap-3">
                                                    <span className="t-text-sm t-font-semibold t-text-automotive-dark">Maximum monthly payment</span>
                                                    <strong className="t-whitespace-nowrap t-text-lg t-text-brand-violet">
                                                        {formatWholeCurrency(maximumMonthlyPayment)}/mo
                                                    </strong>
                                                </span>
                                                <input type="range" min="250" max="1500" step="50" value={maximumMonthlyPayment} onChange={event => setMaximumMonthlyPayment(Number(event.target.value))} className="t-mt-3 t-h-2 t-w-full t-cursor-pointer t-accent-brand-violet" aria-label={`Maximum monthly payment ${formatWholeCurrency(maximumMonthlyPayment)} per month`} />
                                            </label>

                                            <label className="t-block">
                                                <span className="t-flex t-items-baseline t-justify-between t-gap-3">
                                                    <span className="t-text-sm t-font-semibold t-text-automotive-dark">Down payment</span>
                                                    <strong className="t-whitespace-nowrap t-text-lg t-text-brand-violet">
                                                        {formatWholeCurrency(downPayment)}
                                                    </strong>
                                                </span>
                                                <input type="range" min="0" max="10000" step="500" value={downPayment} onChange={event_0 => handleDownPaymentChange(Number(event_0.target.value))} className="t-mt-3 t-h-2 t-w-full t-cursor-pointer t-accent-brand-violet" aria-label={`Down payment ${formatWholeCurrency(downPayment)}`} />
                                            </label>
                                        </div>

                                        <fieldset className="t-mt-7">
                                            <legend className="t-text-sm t-font-semibold t-text-automotive-dark">
                                                About where is your credit score?
                                            </legend>
                                            <div className="t-mt-3 t-grid t-gap-2 sm:t-grid-cols-2">
                                                {CREDIT_PROFILE_IDS.map(profileId => {
                      const profile = PAYMENT_CREDIT_PROFILES[profileId];
                      const active = creditProfileId === profileId;
                      return <button key={profileId} type="button" onClick={() => handleCreditTierChange(profileId)} aria-pressed={active} className={`t-rounded-none t-border t-p-3 t-text-left t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 ${active ? "t-border-brand-violet t-bg-brand-lavender/35" : "t-border-slate-200 t-bg-white hover:t-border-brand-violet/40"}`}>
                                                            <span className="t-flex t-items-center t-justify-between t-gap-3">
                                                                <strong className="t-text-sm t-text-automotive-dark">{profile.label}</strong>
                                                                <span className="t-text-xs t-font-semibold t-text-brand-violet">{profile.range}</span>
                                                            </span>
                                                            <span className="t-mt-1 t-block t-text-xs t-text-brand-gray-medium">{profile.description}</span>
                                                        </button>;
                    })}
                                            </div>
                                        </fieldset>

                                        <fieldset className="t-mt-7">
                                            <legend className="t-flex t-w-full t-items-center t-justify-between t-gap-3 t-text-sm t-font-semibold t-text-automotive-dark">
                                                <span>Loan term</span>
                                                <strong className="t-text-brand-violet">{termMonths} months</strong>
                                            </legend>
                                            <div className="t-mt-3 t-grid t-grid-cols-4 t-gap-2">
                                                {PAYMENT_LOAN_TERMS.map(term_0 => <button key={term_0} type="button" onClick={() => handleTermChange(term_0)} aria-pressed={termMonths === term_0} className={`t-min-h-11 t-rounded-none t-border t-px-2 t-py-2 t-text-sm t-font-semibold t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 ${termMonths === term_0 ? "t-border-brand-violet t-bg-brand-violet t-text-white" : "t-border-slate-200 t-bg-white t-text-automotive-dark hover:t-border-brand-violet/40"}`}>
                                                        {term_0} mo.
                                                    </button>)}
                                            </div>
                                        </fieldset>

                                        <div className="t-mt-7 t-border-l-4 t-border-brand-highlighter t-bg-brand-lavender/25 t-p-4">
                                            <div className="t-flex t-items-start t-gap-3">
                                                <Calculator className="t-mt-0.5 t-h-5 t-w-5 t-flex-shrink-0 t-text-brand-violet" aria-hidden="true" />
                                                <div>
                                                    <p className="t-m-0 t-text-[10px] t-font-black t-uppercase t-tracking-[0.13em] t-text-brand-violet">
                                                        Vehicles within your estimate
                                                    </p>
                                                    <strong className="t-mt-1 t-block t-text-2xl t-text-automotive-dark" aria-live="polite" aria-busy={isCountLoading}>
                                                        {isCountLoading ? "Checking inventory…" : matchingVehicleSummary}
                                                    </strong>
                                                    <p className="t-m-0 t-mt-1 t-text-xs t-leading-5 t-text-brand-gray-medium">
                                                        Based on {formatWholeCurrency(downPayment)} down, {termMonths} months and the {creditProfileId} credit range.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <p className="t-m-0 t-mt-5 t-text-[11px] t-leading-5 t-text-brand-gray-medium">
                                            For shopping purposes only. We use a fixed sample rate for each credit range to calculate the estimate. This is not a credit decision or financing offer. Actual APR, payment, terms and approval are determined by the lender and may vary. Estimated payments exclude tax, title, registration and optional products.
                                        </p>
                                    </div>

                                    <div className="t-flex-shrink-0 t-border-t t-border-slate-200 t-bg-white t-px-5 t-py-4 t-pb-[calc(1rem+env(safe-area-inset-bottom))] sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-4 sm:t-px-7">
                                        <Link href="/finance" className="t-hidden t-text-sm t-font-semibold t-text-brand-violet t-underline-offset-4 hover:t-underline sm:t-inline">
                                            Get pre-qualified for real terms
                                        </Link>
                                        <Button onPress={handleApply} isDisabled={isCountLoading || matchingVehicleCount === 0} aria-label={`Show ${matchingVehicleLabel}`} className="t-inline-flex t-min-h-12 t-w-full t-items-center t-justify-center t-gap-2 t-rounded-none t-bg-brand-highlighter t-px-6 t-py-3 t-text-sm t-font-black t-text-automotive-dark t-transition-colors hover:t-bg-brand-yellow-dark pressed:t-bg-brand-yellow-dark disabled:t-cursor-not-allowed disabled:t-opacity-60 focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 sm:t-w-auto">
                                            {isCountLoading ? "Checking inventory…" : `Show ${matchingVehicleLabel}`}
                                            <ArrowRight className="t-h-4 t-w-4" aria-hidden="true" />
                                        </Button>
                                    </div>
                                </>}
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </section>;
}